import * as React from 'react';
import { Component } from 'react';
import { observable, action, computed, toJS } from 'mobx';
import { observer, inject } from 'mobx-react';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import FundCard from './FundCard';
import FundDocuments from './FundDocuments';
import formatter from "./formatter";
import { stripHtml, sortString, sortDate, sortNumber } from '../utils';
import { NO_DOCUMENTS_MESSAGE } from '../constants';

// Higher-order component to return a shareclass row/card with data and methods required to function
const withShareclass = (WrappedComponent) => {

    @inject('dataStore')
    @observer
    class Shareclass extends Component<any, any> {

        @observable expanded = this.props.dataStore.isExpanded;

        @computed
        get noDataPlaceholder() {
            return this.props.dataStore.config.noDataPlaceholder;
        }

        @computed
        get groupedDocuments() {
            const { documentGroupMeta, cultureAliases, documentGroupOrder } = this.props.dataStore.config;
            const groupedDocuments = groupBy(this.props.shareClass.documents, `meta.${documentGroupMeta}.value`);

            return Object.keys(groupedDocuments)
                .sort(sortString)
                .map(documentGroupName => {
                    const documentGroupNameIndex = documentGroupOrder.indexOf(documentGroupName);
                    return {
                        order: (documentGroupNameIndex > -1) ? documentGroupNameIndex : documentGroupOrder.length,
                        documentGroupName: documentGroupName,
                        documents: groupedDocuments[documentGroupName].sort((a, b) => sortString(
                            get(cultureAliases, a.cultureCode, a.cultureCode),
                            get(cultureAliases, b.cultureCode, b.cultureCode)
                        ))
                    };
                })
                .sort((a, b) => sortNumber(a.order, b.order));
        }

        @computed
        get columns() {
            const values = this.props.columns.map(column => {
                const {
                    type,
                    property,
                    data,
                    columns,
                    groups,
                } = column;

                if (type === "group" && columns.length > 0) {
                    return ({
                        ...column,
                        values: columns.map(c => {
                            if (isArray(toJS(c.data))) {
                                return ({
                                    ...c,
                                    multiple: true,
                                    data: c.data.map(d => (
                                        {
                                            ...d,
                                            value: this.getDataPoint(d)
                                        }
                                    ))
                                });
                            }

                            if (c.data) {
                                return ({
                                    ...c,
                                    data: {
                                        ...c.data,
                                        value: this.getDataPoint(c.data)
                                    }
                                });
                            }
                        })
                    });
                }

                if (type === "dropdown" && groups.length > 0) {
                    return ({
                        ...column,
                        groups: groups.map(g => ({
                            ...g,
                            values: g.columns.map(c => {
                                if (isArray(toJS(c.data))) {
                                    return ({
                                        ...c,
                                        multiple: true,
                                        data: c.data.map(d => (
                                            {
                                                ...d,
                                                value: this.getDataPoint(d)
                                            }
                                        ))
                                    });
                                }
    
                                if (c.data) {
                                    return ({
                                        ...c,
                                        data: {
                                            ...c.data,
                                            value: this.getDataPoint(c.data)
                                        }
                                    });
                                }
                            })
                        }))
                    });
                }

                if (type === 'document') {
                    return ({
                        ...column,
                        value: this.getDocuments(column.documentType)
                    });
                }

                if (isArray(toJS(data))) {
                    return ({
                        ...column,
                        multiple: true,
                        data: data.map(d => (
                            {
                                ...d,
                                value: this.getDataPoint(d)
                            }
                        ))
                    });
                }

                if (data) {
                    return ({
                        ...column,
                        data: {
                            ...data,
                            value: this.getDataPoint(data)
                        }
                    });
                }

                // Support simple label/property column definition
                if (property) {
                    return ({
                        ...column,
                        data: {
                            ...data,
                            value: this.getDataPoint({ property: 'properties_pub', code: property, key: 'value' })
                        }
                    });
                }

                return {
                    ...column,
                    data: { value: this.noDataPlaceholder }
                };
            });

            return values;
        }

        getDataPoint = (dataConfig) => {
            const { 
                property, code, ccy, classification, periodicity, find: findObj, key, index, format, noDataPlaceholder = this.noDataPlaceholder 
            } = dataConfig;

            if (property === 'properties_pub') {
                const value = get(this.props.shareClass, `${property}.${code}.${key}`, noDataPlaceholder);
                return format
                    ? formatter(format.type, this.locale, this.fundCurrency, format.options, value)
                    : value;
            }

            if (property) {
                const dataSet = get(this.props.shareClass, property, []);

                // We should end up with one data object, so get the first item in array
                const data = find(dataSet, {
                    ...(code && { code }),
                    ...(ccy && { ccy }),
                    ...(classification && { classification }),
                    ...(periodicity && { periodicity })
                });

                // Get the values from data
                const { values = [] } = data || {};

                // If we have a find object and key, find and extract a value
                if (findObj && key) {
                    const valueObj = find(values, findObj);
                    const value = get(valueObj, key, noDataPlaceholder);
                    return format
                        ? formatter(format.type, this.locale, this.fundCurrency, format.options, value)
                        : value;
                }

                // If we have a positive index and key, get index and extract a value
                if (index >= 0 && key) {
                    const value = get(values, `[${index}].${key}`, noDataPlaceholder);
                    return format
                        ? formatter(format.type, this.locale, this.fundCurrency, format.options, value)
                        : value;
                }

                // If we have a negative index and key, get last item and extract a value
                if (index < 0 && key) {
                    const value = get(values, `[${values.length - 1}].${key}`, noDataPlaceholder);
                    return format
                        ? formatter(format.type, this.locale, this.fundCurrency, format.options, value)
                        : value;
                }
            }

            return noDataPlaceholder;
        }

        getDataSet = (dataConfig) => {
            const { property, code, ccy, classification, periodicity } = dataConfig;

            if (property) {
                const dataSet = get(this.props.shareClass, property, []);

                // We should end up with one data object, so get the first item in array
                const data = find(dataSet, {
                    ...(code && { code }),
                    ...(ccy && { ccy }),
                    ...(classification && { classification }),
                    ...(periodicity && { periodicity })
                });

                // Get the values from data
                const { values = [] } = data || {};

                return toJS(values);
            }

            return [];
        }

        getDocuments = (documentType) => {
            const { documents = [] } = this.groupedDocuments.find(d => d.documentGroupName === documentType) || {};
            return documents;
        }

        documentSelected = (clientCode) => {
            return this.props.documentDownloads.includes(clientCode);
        }

        getPriceHistory = (columnConfig) => {
            const { data, sort, columns, disclaimerTypes } = columnConfig;

            const headings = columns.map(c => c.label).join(',');

            const dataSet = this.getDataSet(data);

            const disclaimers = this.getDisclaimers(disclaimerTypes).map(d => `"${stripHtml(d)}"`).join('\n');

            if (sort && sort.type === "date") {
                dataSet.sort((a, b) => sortDate(a[sort.key], b[sort.key]));
            }

            if (sort && sort.type === "string") {
                dataSet.sort((a, b) => sortString(a[sort.key], b[sort.key]));
            }

            if (sort && sort.type === "number") {
                dataSet.sort((a, b) => sortNumber(a[sort.key], b[sort.key]));
            }

            if (sort && sort.direction === "DESC") {
                dataSet.reverse();
            }

            let csv = dataSet
                .map(d => {
                    return (
                        columns.map(column => {
                            if (column.data) {
                                return (
                                    this.getDataPoint(column.data)
                                );
                            }
                            if (column.dataKey) {
                                const value = get(d, column.dataKey, this.noDataPlaceholder);
                                return column.format
                                    ? formatter(column.format.type, this.locale, this.fundCurrency, column.format.options, value)
                                    : value;
                            }
                        })
                            .join(',')
                    );
                }
                );

            csv.unshift(headings);
            csv.push(disclaimers);
            csv = csv.join('\n');

            this.props.dataStore.downloadCsv(csv, 'historic_prices.csv');
        }

        getDisclaimers = (disclaimerTypes = []) => {
            const disclaimers = disclaimerTypes
                .map(d => {
                    if (d.linked) {
                        return get(this.disclaimersDictionary, d.disclaimerType);
                    } else {
                        return get(this.props.dataStore.disclaimersDictionary, `*.${d.disclaimerType}`);
                    }
                })
                .filter(d => d !== undefined);

            return disclaimers;
        }

        @computed
        get disclaimersDictionary() {
            const disclaimersDictionary = {};
            const { disclaimers = {} } = this.props.shareClass;

            for (let d in disclaimers) {
                if (d) {
                    const { disclaimer = '' } = disclaimers[d].find(dc => dc.culture === this.props.dataStore.culture) || {};
                    disclaimersDictionary[d] = disclaimer;
                }
            }

            return disclaimersDictionary;
        }

        @computed
        get shareClassDownloads() {
            return this.props.shareClass.documents
                .map(d => d.clientCode)
                .filter(c => this.props.documentDownloads.includes(c));
        }

        @computed
        get downloadCount() {
            return this.shareClassDownloads.length;
        }

        @computed
        get watchListPropertyValue() {
            return get(this.props.shareClass, `properties_pub.${this.props.dataStore.config.watchListProperty}.value`);
        }

        @action
        toggleExpanded = () => {
            this.expanded = !this.expanded;
        }

        @action
        toggleWatchList = () => {
            this.props.dataStore.watchList.includes(this.watchListPropertyValue) ? this.removeWatchList() : this.addWatchList();
        }

        @action
        addWatchList = () => {
            const updatedWatchList = toJS(this.props.dataStore.watchList);
            updatedWatchList.push(this.watchListPropertyValue);
            this.props.dataStore.watchList = updatedWatchList;
        }

        @action
        removeWatchList = () => {
            const updatedWatchList = toJS(this.props.dataStore.watchList);
            this.props.dataStore.watchList = updatedWatchList.filter(value => value !== this.watchListPropertyValue);
        }

        @computed
        get inWatchList() {
            return this.props.dataStore.watchList.includes(this.watchListPropertyValue);
        }

        @computed
        get fundCurrency() {
            return get(this.props.shareClass, `properties_pub.${this.props.dataStore.config.fundCurrencyProperty || 'ccy'}.value`);
        }

        @computed
        get locale() {
            return this.props.dataStore.culture;
        }

        @computed
        get fundCardDisclaimers() {
            return this.getDisclaimers(this.props.dataStore.config.rowConfig.disclaimers);
        }

        @computed
        get noDocumentsDisclaimers() {
            const { noDocumentsDisclaimers = [] } = this.props.dataStore.config.rowConfig;
            return noDocumentsDisclaimers.length > 0 
                ? this.getDisclaimers(noDocumentsDisclaimers) 
                : [NO_DOCUMENTS_MESSAGE];
        }

        render() {
            // TODO: Is there a DRYer way to get these as props on to the WrappedComponent?
            // Spreading {...this} doesn't work
            const {
                expanded,
                columns,
                shareClassDownloads,
                downloadCount,
                documentSelected,
                getPriceHistory,
                watchListPropertyValue,
                toggleExpanded,
                toggleWatchList,
                addWatchList,
                removeWatchList,
                inWatchList
            } = this;

            return (
                <WrappedComponent
                    {...this.props}
                    {
                    ...{
                        expanded,
                        columns,
                        shareClassDownloads,
                        downloadCount,
                        documentSelected,
                        getPriceHistory,
                        watchListPropertyValue,
                        toggleExpanded,
                        toggleWatchList,
                        addWatchList,
                        removeWatchList,
                        inWatchList
                    }
                    }
                >
                    {
                        this.props.rowType === 'FundCard' && (
                            <FundCard
                                isMobile={this.props.isMobile}
                                shareClass={this.props.shareClass}
                                fundCardDisclaimers={this.fundCardDisclaimers}
                                groupedDocuments={this.groupedDocuments}
                            />
                        )
                    }
                    {
                        this.props.rowType === 'Documents' && (
                            <FundDocuments
                                groupedDocuments={this.groupedDocuments}
                                documentDownloads={this.props.documentDownloads}
                                toggleDownload={this.props.toggleDownload}
                                documentSelected={this.documentSelected}
                                fundCardDisclaimers={this.fundCardDisclaimers}
                                noDocumentsDisclaimers={this.noDocumentsDisclaimers}
                            />
                        )
                    }
                </WrappedComponent>
            );
        }
    }

    return Shareclass;
};

export default withShareclass;


// WEBPACK FOOTER //
// ./src/Components/withShareclass.tsx